import {
	DocSquareIcon,
	ExtensionSquareIcon,
	NewSquareIcon,
} from '@dealstack/frontend/shared/components/DocumentCard/Components/SquareTextIcons';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import {
	DefaultDropdownMenu,
	Dropdown,
} from '@dealstack/frontend/shared/components/fundament/Dropdown/Dropdown';
import { DropdownWrapper } from '@dealstack/frontend/shared/components/fundament/Dropdown/DropdownWrapper';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useOutsideClick } from '@dealstack/frontend/shared/other/react/hooks/useOutsideClick';
import { useGetComponentsFromChildren } from '@dealstack/shared/lib/useGetComponentsFromChildren';
import { DOCUMENT_WORKFLOW } from '@dealstack/shared/tests/testElementIds';
import { Badge, BadgeProps, Checkbox, rem, Tooltip } from '@mantine/core';
import { ComponentType, memo, PropsWithChildren, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import type { IDocumentCardProps } from './DocumentCard.types';

const DocumentCardComponent = ({
	className,
	documentTitle,
	date,
	showDate,
	onClickCard,
	onClickCheckbox,
	checkboxValue,
	documentID,
	disabled,
	isNew,
	children,
	leftIcon,
	onClickDropdown,
	isDropdownOpen,
	onCloseDropdown,
	fileExtension,
	valuationsDateFontSizeVariant,
}: PropsWithChildren<IDocumentCardProps>) => {
	const [dropdownMenu, statusChip] = useGetComponentsFromChildren(children, [
		DocumentCard.DropdownMenu,
		DocumentCard.StatusChip,
	]);

	const [showDropdown, setShowDropdown] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	useOutsideClick<HTMLDivElement>(ref, () => {
		setShowDropdown(false);
	});

	return (
		<div
			data-testid={`${DOCUMENT_WORKFLOW.DOCUMENT_NAME}-${documentTitle}`}
			className={className}
			onClick={(e: React.MouseEvent<HTMLDivElement>) => {
				const target = e.target as HTMLInputElement;
				if (target && target.type !== 'checkbox') {
					if (!onClickCard || disabled) {
						return;
					}
					onClickCard(documentID);
				}
			}}
		>
			<DocumentHeader>
				{checkboxValue !== undefined && (
					<CheckboxWrapper>
						<Checkbox
							onChange={() => {
								if (!onClickCheckbox) {
									return;
								}
								onClickCheckbox(documentID);
							}}
							checked={checkboxValue}
						/>
					</CheckboxWrapper>
				)}

				<DocumentDataWrapper>
					{leftIcon}
					<div>
						<Tooltip
							label={documentTitle}
							withinPortal={true}
							zIndex={1000}
							position="top"
						>
							<TextV2
								variant="sans/14/600"
								multiLineElipsis={{ enabled: true, numberOfLines: 2 }}
							>
								{documentTitle}
							</TextV2>
						</Tooltip>

						{showDate ? (
							<DocumentDateText
								variant={
									valuationsDateFontSizeVariant ? 'sans/14/400' : 'sans/11/400'
								}
							>
								{date}
							</DocumentDateText>
						) : null}
					</div>
				</DocumentDataWrapper>
				<IconsWrapper>
					<SquareIconWrapper>
						{isNew ? <NewSquareIcon /> : null}
						<ExtensionSquareIcon extension={fileExtension} />
					</SquareIconWrapper>

					<MoreOptions
						variant="dots-horiz"
						onClick={(event) => {
							event.stopPropagation();
							if (isDropdownOpen && showDropdown) {
								setShowDropdown(false);
								if (onCloseDropdown) {
									onCloseDropdown();
								}
								return;
							}
							setShowDropdown(true);
							if (!onClickDropdown) {
								return;
							}
							onClickDropdown();
						}}
					/>
					{dropdownMenu && (
						<DropdownWrapper>
							{isDropdownOpen || showDropdown ? (
								<Dropdown
									onClose={() => {
										setShowDropdown(false);
										if (!onCloseDropdown) {
											return;
										}
										onCloseDropdown();
									}}
								>
									{dropdownMenu}
								</Dropdown>
							) : null}
						</DropdownWrapper>
					)}
				</IconsWrapper>
			</DocumentHeader>
			{statusChip}
		</div>
	);
};

const MoreOptions = styled(Icon)`
	margin-left: 1rem;
	display: none;
	right: 1rem;
	&.svg {
		transform: rotate(-90deg);
		--size: 1.5rem;
	}
`;

const DocumentHeader = styled.div`
	display: flex;
	align-items: flex-start;
`;
const DocumentDataWrapper = styled.div`
	width: 100%;
	display: flex;
	gap: 0.5rem;
	align-items: baseline;
`;

const DocumentDateText = styled(TextV2)`
	color: ${({ theme }) => theme.v2.color.neutral[700]};
`;

const IconsWrapper = styled.div`
	display: flex;
	flex-direction: row;
	min-width: 2.2rem;
	align-content: flex-end;
	min-height: 1.5rem;
	justify-content: flex-end;
	margin-left: 0.5rem;
`;
const SquareIconWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
`;
const CheckboxWrapper = styled.div`
	margin: 0.125rem 0.625rem 0 0.125rem;
`;

const SwitchIconsAndMoreOptionsCSS = css`
	${DocumentHeader} {
		${IconsWrapper} {
			${SquareIconWrapper} {
				display: none;
			}
			${MoreOptions} {
				display: flex;
			}
		}
	}
`;

const getHoveredBackgroundColor = (
	disabled: boolean,
	isSelected: boolean,
	isUploaded: boolean,
	isWarning: boolean
) => {
	if (disabled) {
		return defaultTheme.color.white;
	}
	if (isSelected) {
		return defaultTheme.v2.color.secondary[200];
	} else if (isWarning) {
		return defaultTheme.v2.color.warning[400];
	} else if (isUploaded) {
		return defaultTheme.v2.color.success[400];
	} else {
		return defaultTheme.v2.color.neutral[100];
	}
};

const getStandardBackgroundColor = (
	disabled: boolean,
	isSelected: boolean,
	isUploaded: boolean,
	isWarning: boolean
) => {
	if (disabled) {
		return defaultTheme.color.white[500];
	}
	if (isSelected) {
		return defaultTheme.v2.color.secondary[200];
	} else if (isWarning) {
		return defaultTheme.v2.color.warning[200];
	} else if (isUploaded) {
		return defaultTheme.v2.color.success[200];
	} else {
		return defaultTheme.color.white[500];
	}
};

const Memoized = memo(DocumentCardComponent);
const Styled = styled(Memoized)`
	flex-grow: 0;
	display: flex;
	flex-direction: column;
	gap: ${rem(8)};
	padding: ${rem(8)};
	border-radius: ${rem(4)};

	min-width: 8rem;
	width: ${(props) => (props.fullWidth === false ? 'fit-content' : 'auto')};

	${DocumentDateText} {
		margin-top: 0.25rem;
	}
	${CheckboxWrapper} {
		min-width: 1.25rem;
	}
	border: solid 1px
		${({ theme, isSelected, isWarning, isUploaded }) => {
			if (isSelected) {
				return theme.v2.color.secondary[600];
			}
			if (isWarning) {
				return theme.v2.color.warning[600];
			}
			if (isUploaded) {
				return theme.v2.color.success[600];
			}
			return theme.v2.color.neutral[200];
		}};

	background-color: ${({
		isSelected = false,
		isUploaded = false,
		isWarning = false,
		disabled = false,
	}) => getStandardBackgroundColor(disabled, isSelected, isUploaded, isWarning)};

	${(props) =>
		props.disabled
			? css`
					opacity: 0.32;
					pointer-events: none;
				`
			: props.onClickCard &&
				css`
					cursor: pointer;
				`}
	${DropdownWrapper} {
		position: relative;
		z-index: 10;
	}
	${DocSquareIcon} {
		margin-left: 0.5rem;
	}

	${DefaultDropdownMenu} {
		padding: 0.5em;
		background-color: ${({ theme }) => theme.color.white[500]};
		box-shadow: 0 2px 4px 0 ${({ theme }) => theme.v2.color.neutral['300']};
		border-radius: 4px;
		width: 6.75em;
	}

	&:hover {
		border: solid 1px
			${({ theme, isSelected }) =>
				isSelected ? theme.v2.color.secondary[600] : theme.v2.color.neutral[300]};

		background-color: ${({
			isSelected = false,
			disabled = false,
			isWarning = false,
			isUploaded = false,
		}) => getHoveredBackgroundColor(disabled, isSelected, isUploaded, isWarning)};

		${({ children }) => {
			const [dropdownMenu] = useGetComponentsFromChildren(children, [
				DocumentCard.DropdownMenu,
			]);
			if (dropdownMenu) {
				return css`
					${SwitchIconsAndMoreOptionsCSS}
				`;
			}
			return;
		}}
	}
`;

// Unified standard with our Mantine library
// see example here: https://github.com/mantinedev/mantine/blob/master/src/mantine-core/src/Stepper/Stepper.tsx
type DocumentCardType = typeof DocumentCardComponent & {
	DropdownMenu: typeof DefaultDropdownMenu;
	StatusChip: ComponentType<PropsWithChildren<BadgeProps>>;
};
// Fix for styled components (they read only props not whole type)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DocumentCard: DocumentCardType = Styled as any;
DocumentCard.DropdownMenu = DefaultDropdownMenu;
const StatusChipBadge = (props: PropsWithChildren<BadgeProps>) => <Badge {...props} />;
DocumentCard.StatusChip = StatusChipBadge;
